/**
 * WORKSPACE STORE
 *
 * Manages workspaces - multiple "worlds" that contain projects, agents, and zones.
 * Users can switch between workspaces to view different environments.
 *
 * Supports two workspace types:
 * - "cloud" (default): persisted to backend API
 * - "local": persisted to SQLite (Tauri desktop only)
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "@/lib/api";
import { isTauri } from "@/hooks/usePlatform";
import { getDataSource } from "@/lib/data";

export type WorkspaceType = "cloud" | "local";

export type BillingMode = "byok" | "credits";

export type ContainerStatus = "none" | "creating" | "active" | "sleeping" | "waking" | "error";

export interface Workspace {
  id: string;
  backendId?: number;
  name: string;
  description: string;
  color: string;
  icon: string; // "colony" for Colony logo, emoji string otherwise
  projectCount: number;
  agentCount: number;
  createdAt: string;
  type?: WorkspaceType; // default "cloud" for backward compat
  githubLogin?: string;
  githubAvatarUrl?: string;
  linkedWorkspaceId?: number; // cloud workspace backend ID this local ws is linked to
  billingMode?: BillingMode; // "byok" (default) or "credits"
  containerStatus?: ContainerStatus;
  containerIp?: string;
  containerPort?: number;
  autoSleepMinutes?: number;
}

/** Check if a workspace ID belongs to a local workspace */
export function isLocalWorkspace(id: string): boolean {
  return id.startsWith("local:") || id.startsWith("local-stub-");
}

/** Get the workspace type from an ID */
export function getWorkspaceType(ws: Workspace | undefined): WorkspaceType {
  if (!ws) return "cloud";
  return ws.type ?? (isLocalWorkspace(ws.id) ? "local" : "cloud");
}

interface WorkspaceState {
  workspaces: Workspace[];
  activeWorkspaceId: string;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  saveError: string | null;

  setActiveWorkspace: (id: string) => void;
  ensureWorkspaceReady: () => Promise<number | null>;
  fetchWorkspaces: () => Promise<void>;
  addWorkspace: (workspace: Omit<Workspace, "id" | "createdAt" | "projectCount" | "agentCount">, type?: WorkspaceType) => Promise<Workspace | null>;
  removeWorkspace: (id: string) => Promise<boolean>;
  updateWorkspace: (id: string, updates: Partial<Workspace>) => Promise<boolean>;
  getActiveWorkspace: () => Workspace | undefined;
  getActiveWorkspaceBackendId: () => number | null;
  getBillingWorkspaceId: () => number | null;
  getActiveWorkspaceType: () => WorkspaceType;
  getEffectiveWorkspaceId: () => number | string | null;
  updateBillingMode: (id: string, mode: BillingMode) => Promise<void>;
  createContainer: (workspaceId: string) => Promise<void>;
  wakeContainer: (workspaceId: string) => Promise<void>;
  sleepContainer: (workspaceId: string) => Promise<void>;
  deleteContainer: (workspaceId: string) => Promise<void>;
  getContainerStatus: (workspaceId: string) => Promise<ContainerStatus>;
  clearSaveError: () => void;
}

type ApiWorkspace = {
  id: number;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  workspace_type?: string;
  created_at?: string;
  github_login?: string;
  github_avatar_url?: string;
  container_status?: string;
  container_ip?: string;
  container_port?: number;
  auto_sleep_minutes?: number;
};

function mapApiWorkspace(ws: ApiWorkspace): Workspace {
  const wsType: WorkspaceType = ws.workspace_type === "local" ? "local" : "cloud";
  return {
    id: wsType === "local" ? `local-stub-${ws.id}` : `ws-${ws.id}`,
    backendId: ws.id,
    name: ws.name,
    description: ws.description || "",
    color: ws.color || "#6366f1",
    icon: ws.icon || "colony",
    projectCount: 0,
    agentCount: 0,
    createdAt: ws.created_at || new Date().toISOString(),
    type: wsType,
    githubLogin: ws.github_login || undefined,
    githubAvatarUrl: ws.github_avatar_url || undefined,
    containerStatus: (ws.container_status as ContainerStatus) || "none",
    containerIp: ws.container_ip || undefined,
    containerPort: ws.container_port || undefined,
    autoSleepMinutes: ws.auto_sleep_minutes || 30,
  };
}

function mapLocalWorkspace(ws: any): Workspace {
  return {
    id: ws.id,
    name: ws.name,
    description: ws.description || "",
    color: ws.color || "#6366f1",
    icon: ws.icon || "colony",
    projectCount: 0,
    agentCount: 0,
    createdAt: ws.created_at || new Date().toISOString(),
    type: "local",
    linkedWorkspaceId: ws.linked_workspace_id ?? undefined,
    billingMode: ws.billing_mode ?? "byok",
  };
}

/** Side-effect: sync local data source context when switching to a local workspace */
function syncLocalContext(id: string) {
  if (id && isLocalWorkspace(id)) {
    import("@/lib/data/local-source").then(({ setActiveLocalWorkspace }) => {
      setActiveLocalWorkspace(id);
    }).catch(() => {});
  }
}

// ── Per-workspace navigation state persistence ──────────────────────

function wsNavKey(workspaceId: string, suffix: string) {
  return `factory-ws-${suffix}-${workspaceId}`;
}

export function getWorkspaceRoute(workspaceId: string): string | null {
  try { return localStorage.getItem(wsNavKey(workspaceId, "route")); } catch { return null; }
}

export function setWorkspaceRoute(workspaceId: string, route: string) {
  try { localStorage.setItem(wsNavKey(workspaceId, "route"), route); } catch {}
}

export function getWorkspaceTab(workspaceId: string): string | null {
  try { return localStorage.getItem(wsNavKey(workspaceId, "tab")); } catch { return null; }
}

export function setWorkspaceTab(workspaceId: string, tab: string) {
  try { localStorage.setItem(wsNavKey(workspaceId, "tab"), tab); } catch {}
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      workspaces: [],
      activeWorkspaceId: "",
      isLoading: false,
      isSaving: false,
      error: null,
      saveError: null,

      setActiveWorkspace: (id) => {
        const previousId = get().activeWorkspaceId;
        set({ activeWorkspaceId: id });
        syncLocalContext(id);

        // Reset git store when switching workspaces to prevent stale credentials
        if (id !== previousId) {
          import("@/stores/gitStore").then(({ useGitStore }) => {
            useGitStore.getState().reset();
          }).catch(() => {});
        }
      },

      ensureWorkspaceReady: async () => {
        // Always fetch from API to ensure we have current user's workspaces
        // (localStorage may have stale data from a different user session)
        await get().fetchWorkspaces();

        // Filter out local stubs — they can't be active workspaces on the web
        const selectableWorkspaces = get().workspaces.filter(
          (w) => !w.id.startsWith("local-stub-")
        );

        // After fetch, if no selectable workspaces, create a default workspace
        if (selectableWorkspaces.length === 0) {
          const created = await get().addWorkspace({
            name: "Default Workspace",
            description: "",
            color: "#3b82f6",
            icon: "colony",
          });
          if (created) {
            set({ activeWorkspaceId: created.id });
            syncLocalContext(created.id);
            return created.backendId ?? null;
          }
          console.warn("[workspaceStore] No workspace available - API may be returning empty data");
          return null;
        }

        // Ensure activeWorkspaceId is set to a selectable workspace
        const currentActive = selectableWorkspaces.find((w) => w.id === get().activeWorkspaceId);
        if (!currentActive) {
          const first = selectableWorkspaces[0];
          set({ activeWorkspaceId: first.id });
          syncLocalContext(first.id);
          return first.backendId ?? null;
        }

        return currentActive.backendId ?? null;
      },

      fetchWorkspaces: async () => {
        set({ isLoading: true, error: null });

        // Fetch local workspaces FIRST (independent of cloud API)
        let localWorkspaces: Workspace[] = [];
        if (isTauri()) {
          try {
            const ds = await getDataSource("local");
            const localData = await ds.workspaces.list();
            localWorkspaces = localData.map(mapLocalWorkspace);
          } catch (err) {
            console.warn("[workspaceStore] Failed to load local workspaces:", err);
          }
        }

        // Fetch cloud workspaces from API (may fail if not authenticated)
        let cloudWorkspaces: Workspace[] = [];
        let cloudError: string | null = null;
        try {
          const response = await api.get<ApiWorkspace[]>("/api/workspaces");
          cloudWorkspaces = (response && response.length > 0)
            ? response.map(mapApiWorkspace)
            : [];
        } catch (error) {
          cloudError = (error as Error).message;
          console.warn("[workspaceStore] Failed to load cloud workspaces:", cloudError);
        }

        // Merge cloud + local workspaces
        const allWorkspaces = [...cloudWorkspaces, ...localWorkspaces];

        // Validate activeWorkspaceId - reset if it doesn't exist in the new list
        const currentActiveId = get().activeWorkspaceId;
        const isActiveValid = allWorkspaces.some((w) => w.id === currentActiveId);

        if (allWorkspaces.length > 0) {
          // In Tauri, prefer local workspace when auto-selecting
          const fallback = (isTauri() && localWorkspaces.length > 0)
            ? localWorkspaces[0].id
            : allWorkspaces[0].id;
          const newActiveId = isActiveValid ? currentActiveId : fallback;
          set({
            workspaces: allWorkspaces,
            activeWorkspaceId: newActiveId,
            isLoading: false,
            error: cloudError,
          });
          syncLocalContext(newActiveId);
        } else {
          set({
            workspaces: [],
            activeWorkspaceId: "",
            error: cloudError,
            isLoading: false,
          });
        }
      },

      addWorkspace: async (workspace, type = "cloud") => {
        set({ isSaving: true, saveError: null });
        try {
          if (type === "local") {
            if (!isTauri()) {
              throw new Error("Local workspaces require the desktop app");
            }
            // Auto-link to the first cloud workspace with a backendId
            const cloudWs = get().workspaces.find((w) => w.type !== "local" && w.backendId);
            const linkedId = cloudWs?.backendId ?? null;

            const ds = await getDataSource("local");
            const created = await ds.workspaces.create({
              name: workspace.name,
              description: workspace.description,
              color: workspace.color,
              linkedWorkspaceId: linkedId,
            } as any);
            const mapped = mapLocalWorkspace(created);
            set((state) => ({
              workspaces: [...state.workspaces, mapped],
              activeWorkspaceId: mapped.id,
              isSaving: false,
            }));
            syncLocalContext(mapped.id);
            return mapped;
          }

          // Cloud workspace
          const response = await api.post<ApiWorkspace>("/api/workspaces", {
            name: workspace.name,
            description: workspace.description,
            color: workspace.color,
            icon: workspace.icon || "colony",
          });
          const mapped = mapApiWorkspace(response);
          set((state) => ({
            workspaces: [...state.workspaces, mapped],
            activeWorkspaceId: mapped.id,
            isSaving: false,
          }));
          return mapped;
        } catch (error) {
          const msg = error instanceof Error ? error.message : String(error);
          console.error("[workspaceStore] addWorkspace failed:", error);
          set({ saveError: msg || "Unknown error creating workspace", isSaving: false });
          return null;
        }
      },

      removeWorkspace: async (id) => {
        const state = get();
        const workspace = state.workspaces.find((w) => w.id === id);
        if (!workspace) return false;

        set({ isSaving: true, saveError: null });
        try {
          if (isLocalWorkspace(id) && !workspace.backendId) {
            const ds = await getDataSource("local");
            await ds.workspaces.delete(id);
          } else {
            if (!workspace.backendId) {
              console.error("No backend ID found for workspace:", id);
              return false;
            }
            await api.delete(`/api/workspaces/${workspace.backendId}`);
          }

          const wasActive = get().activeWorkspaceId === id;
          set((state) => ({
            workspaces: state.workspaces.filter((w) => w.id !== id),
            activeWorkspaceId:
              state.activeWorkspaceId === id
                ? state.workspaces.filter((w) => w.id !== id)[0]?.id || ""
                : state.activeWorkspaceId,
            isSaving: false,
          }));
          if (wasActive) {
            syncLocalContext(get().activeWorkspaceId);
          }
          return true;
        } catch (error) {
          console.error("Failed to delete workspace:", error);
          set({ saveError: (error as Error).message, isSaving: false });
          return false;
        }
      },

      updateWorkspace: async (id, updates) => {
        const state = get();
        const workspace = state.workspaces.find((w) => w.id === id);
        if (!workspace) return false;

        set({ isSaving: true, saveError: null });
        try {
          if (isLocalWorkspace(id) && !workspace.backendId) {
            const ds = await getDataSource("local");
            await ds.workspaces.update(id, {
              name: updates.name,
              description: updates.description,
              color: updates.color,
            });
          } else {
            if (!workspace.backendId) {
              console.error("No backend ID found for workspace:", id);
              return false;
            }
            const apiUpdates: Record<string, unknown> = {};
            if (updates.name !== undefined) apiUpdates.name = updates.name;
            if (updates.description !== undefined) apiUpdates.description = updates.description;
            if (updates.color !== undefined) apiUpdates.color = updates.color;
            if (updates.icon !== undefined) apiUpdates.icon = updates.icon;
            await api.patch<Workspace>(`/api/workspaces/${workspace.backendId}`, apiUpdates);
          }

          set((state) => ({
            workspaces: state.workspaces.map((w) =>
              w.id === id ? { ...w, ...updates } : w
            ),
            isSaving: false,
          }));
          return true;
        } catch (error) {
          console.error("Failed to update workspace:", error);
          set({ saveError: (error as Error).message, isSaving: false });
          return false;
        }
      },

      getActiveWorkspace: () => {
        const state = get();
        return state.workspaces.find((w) => w.id === state.activeWorkspaceId);
      },

      getActiveWorkspaceBackendId: () => {
        const state = get();
        const workspace = state.workspaces.find((w) => w.id === state.activeWorkspaceId);
        if (!workspace) return null;
        const wsType = getWorkspaceType(workspace);
        if (wsType === "cloud") return workspace.backendId ?? null;
        // Local workspace: use linked cloud workspace ID
        return workspace.linkedWorkspaceId ?? null;
      },

      getBillingWorkspaceId: () => {
        const state = get();
        const workspace = state.workspaces.find((w) => w.id === state.activeWorkspaceId);
        if (!workspace) return null;
        const wsType = getWorkspaceType(workspace);
        if (wsType === "cloud") return workspace.backendId ?? null;
        // Local workspace: use linked cloud workspace ID
        return workspace.linkedWorkspaceId ?? null;
      },

      getActiveWorkspaceType: () => {
        const state = get();
        const workspace = state.workspaces.find((w) => w.id === state.activeWorkspaceId);
        if (!workspace && isLocalWorkspace(state.activeWorkspaceId)) return "local";
        return getWorkspaceType(workspace);
      },

      getEffectiveWorkspaceId: () => {
        const state = get();
        const wsType = state.getActiveWorkspaceType();
        if (wsType === "local") return state.activeWorkspaceId;
        return state.getActiveWorkspaceBackendId();
      },

      updateBillingMode: async (id, mode) => {
        if (!isLocalWorkspace(id)) return;
        try {
          const ds = await getDataSource("local");
          await ds.workspaces.update(id, { billingMode: mode } as any);
          set((state) => ({
            workspaces: state.workspaces.map((w) =>
              w.id === id ? { ...w, billingMode: mode } : w
            ),
          }));
        } catch (error) {
          console.error("[workspaceStore] updateBillingMode failed:", error);
        }
      },

      createContainer: async (workspaceId: string) => {
        const ws = get().workspaces.find(w => w.id === workspaceId);
        if (!ws?.backendId) return;
        await api.post(`/api/workspaces/${ws.backendId}/container`);
        set(state => ({
          workspaces: state.workspaces.map(w =>
            w.id === workspaceId ? { ...w, containerStatus: "creating" as ContainerStatus } : w
          ),
        }));
      },

      wakeContainer: async (workspaceId: string) => {
        const ws = get().workspaces.find(w => w.id === workspaceId);
        if (!ws?.backendId) return;
        await api.post(`/api/workspaces/${ws.backendId}/container/wake`);
        set(state => ({
          workspaces: state.workspaces.map(w =>
            w.id === workspaceId ? { ...w, containerStatus: "waking" as ContainerStatus } : w
          ),
        }));
      },

      sleepContainer: async (workspaceId: string) => {
        const ws = get().workspaces.find(w => w.id === workspaceId);
        if (!ws?.backendId) return;
        await api.post(`/api/workspaces/${ws.backendId}/container/sleep`);
        set(state => ({
          workspaces: state.workspaces.map(w =>
            w.id === workspaceId ? { ...w, containerStatus: "sleeping" as ContainerStatus } : w
          ),
        }));
      },

      deleteContainer: async (workspaceId: string) => {
        const ws = get().workspaces.find(w => w.id === workspaceId);
        if (!ws?.backendId) return;
        await api.delete(`/api/workspaces/${ws.backendId}/container`);
        set(state => ({
          workspaces: state.workspaces.map(w =>
            w.id === workspaceId ? { ...w, containerStatus: "none" as ContainerStatus } : w
          ),
        }));
      },

      getContainerStatus: async (workspaceId: string) => {
        const ws = get().workspaces.find(w => w.id === workspaceId);
        if (!ws?.backendId) return "none" as ContainerStatus;
        const data = await api.get<{ container_status: string; container_ip?: string }>(`/api/workspaces/${ws.backendId}/container/status`);
        const status = data.container_status as ContainerStatus;
        set(state => ({
          workspaces: state.workspaces.map(w =>
            w.id === workspaceId ? { ...w, containerStatus: status, containerIp: data.container_ip || undefined } : w
          ),
        }));
        return status;
      },

      clearSaveError: () => {
        set({ saveError: null });
      },
    }),
    {
      name: "factory-workspaces",
      version: 4,
      migrate: (persistedState: any, version) => {
        // Clear stale data when migrating to a new version
        if (version < 4) {
          return {
            workspaces: [],
            activeWorkspaceId: "",
            isLoading: false,
            isSaving: false,
            error: null,
            saveError: null,
          };
        }
        return persistedState as WorkspaceState;
      },
    }
  )
);
